import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CreateTemplateDialog } from "../CreateTemplateDialog";
import { EditTemplateDialog } from "../EditTemplateDialog";
import { ViewTemplateDialog } from "../ViewTemplateDialog";
import { toast } from "sonner";
import { NewsletterTemplate, TemplateListProps } from "../types";
import { TemplateSearchBar } from "./components/TemplateSearchBar";
import { TemplateGrid } from "./components/TemplateGrid";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const TemplateList = ({ onSelect, onPreviewTemplate }: TemplateListProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NewsletterTemplate | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  const queryClient = useQueryClient();

  const { data: templateData, isLoading } = useQuery({
    queryKey: ['newsletter-templates', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('newsletter_templates')
        .select(`
          *,
          newsletter_template_versions (*)
        `)
        .order('created_at', { ascending: false });
      
      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }
      
      const { data, error } = await query;
      if (error) {
        toast.error("Failed to load templates");
        throw error;
      }

      return data.map(template => ({
        ...template,
        content_structure: typeof template.content_structure === 'object' ? {
          template: String((template.content_structure as any)?.template || ''),
          variables: ((template.content_structure as any)?.variables || {})
        } : {
          template: '',
          variables: {}
        },
        newsletter_template_versions: template.newsletter_template_versions?.map((version: any) => ({
          ...version,
          content_structure: typeof version.content_structure === 'object' ? {
            template: String((version.content_structure as any)?.template || ''),
            variables: ((version.content_structure as any)?.variables || {})
          } : {
            template: '',
            variables: {}
          }
        }))
      })) as NewsletterTemplate[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('newsletter_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] });
      toast.success("Template deleted successfully");
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
    },
    onError: (error) => {
      console.error('Error deleting template:', error);
      toast.error("Failed to delete template");
    }
  });

  const handleEdit = (template: NewsletterTemplate) => {
    setSelectedTemplate(template);
    setShowEditDialog(true);
  };

  const handleView = (template: NewsletterTemplate) => {
    setSelectedTemplate(template);
    setShowViewDialog(true);
  };

  const handleDeletePrompt = (template: NewsletterTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteDialog(true);
  };

  const handleDelete = () => {
    if (selectedTemplate) {
      deleteMutation.mutate(selectedTemplate.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <TemplateSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {!templateData?.length ? (
        <div className="text-center py-8 text-muted-foreground">
          No templates found
        </div>
      ) : (
        <TemplateGrid
          templates={templateData}
          onEdit={handleEdit}
          onView={handleView}
          onSelect={onSelect}
          onDelete={handleDeletePrompt}
        />
      )}

      <CreateTemplateDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog}
      />

      {selectedTemplate && (
        <>
          <EditTemplateDialog
            template={selectedTemplate}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
          />
          <ViewTemplateDialog
            template={selectedTemplate}
            open={showViewDialog}
            onOpenChange={setShowViewDialog}
          />
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the template
                  "{selectedTemplate.name}".
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
};